import { useCallback, useLayoutEffect, useRef, useState } from 'react';

type Query<T> = () => Promise<T>;
type QueryState<T> = {
  data: T;
  loading: boolean;
  error?: Error;
  query?: Query<T>;
};

/** One request generation owns all state, including errors and completion. */
export function usePortfolioRequest<T>(query: Query<T>, initialData: T) {
  const [state, setState] = useState<QueryState<T>>({
    data: initialData,
    loading: true,
  });
  const runner = useRef<() => Promise<void>>();
  const refresh = useCallback(
    () => runner.current?.() ?? Promise.resolve(),
    [],
  );

  // Switch the refresh target at commit, before passive filter reconciliation.
  // Retained callbacks from administrative operations use the latest query.
  useLayoutEffect(() => {
    let generation = 0;
    let active = true;
    const run = async () => {
      const request = ++generation;
      setState(previous => ({
        ...previous,
        query,
        loading: true,
        error: undefined,
      }));
      try {
        const data = await query();
        if (active && request === generation) {
          setState({ data, query, loading: false });
        }
      } catch (cause) {
        if (active && request === generation) {
          const error =
            cause instanceof Error ? cause : new Error(String(cause));
          setState(previous => ({ ...previous, query, error, loading: false }));
        }
      }
    };
    runner.current = run;
    void run();
    return () => {
      active = false;
      runner.current = undefined;
    };
  }, [query]);

  const current = state.query === query;
  return {
    data: state.data,
    error: current ? state.error : undefined,
    loading: !current || state.loading,
    ready: current && !state.loading && !state.error,
    refresh,
  };
}
